import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  TagIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';
import { WishListItem, WishListStats } from '../types/wishlist';
import WishListForm from './forms/WishListForm';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const WishList: React.FC = () => {
  const [items, setItems] = useState<WishListItem[]>([]);
  const [stats, setStats] = useState<WishListStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WishListItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterPurchased, setFilterPurchased] = useState<string>('');
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};

      if (searchQuery) params.search = searchQuery;
      if (filterPriority) params.priority = filterPriority;
      if (filterPurchased !== '') params.is_purchased = filterPurchased;

      const response = await apiClient.get('/api/wishlist/', { params });
      setItems(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching wish list items:', err);
      setError('Failed to load wish list items');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterPriority, filterPurchased]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/api/wishlist/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching wish list stats:', err);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [searchQuery, filterPriority, filterPurchased, fetchItems]);

  const handleMarkPurchased = async (item: WishListItem) => {
    try {
      const endpoint = item.is_purchased ? 'mark_not_purchased' : 'mark_purchased';
      await apiClient.post(`/api/wishlist/${item.id}/${endpoint}/`);
      fetchItems();
      fetchStats();
    } catch (err) {
      console.error('Error updating purchase status:', err);
    }
  };

  const handleEditItem = (item: WishListItem) => {
    setEditingItem(item);
    setIsFormOpen(true);
  };

  const handleDeleteItem = async (item: WishListItem) => {
    if (window.confirm(`Are you sure you want to delete "${item.title}"?`)) {
      try {
        await apiClient.delete(`/api/wishlist/${item.id}/`);
        fetchItems();
        fetchStats();
      } catch (err) {
        console.error('Error deleting wish list item:', err);
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingItem(null);
  };

  const handleItemCreated = () => {
    fetchItems();
    fetchStats();
  };

  const getPriorityStyles = (priority: string): React.CSSProperties => {
    switch (priority) {
      case 'urgent': return { color: '#DC2626', backgroundColor: '#FEE2E2' };
      case 'high': return { color: '#EA580C', backgroundColor: '#FFEDD5' };
      case 'medium': return { color: '#CA8A04', backgroundColor: '#FEF9C3' };
      case 'low': return { color: '#16A34A', backgroundColor: '#DCFCE7' };
      default: return { color: COLORS.text.secondary, backgroundColor: COLORS.bg.hover };
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <ArrowPathIcon style={{ width: '24px', height: '24px', color: COLORS.text.tertiary, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.weight.bold,
              color: COLORS.text.primary,
              margin: 0
            }}>
              Wish List
            </h1>
            <p style={{ color: COLORS.text.secondary, margin: 0, marginTop: SPACING[4] }}>
              Things you'd like to purchase
            </p>
          </div>
          <button
            onClick={() => setIsFormOpen(true)}
            onMouseEnter={() => setHoveredButton('add-header')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[8]} ${SPACING[16]}`,
              border: 'none',
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: '#FFFFFF',
              backgroundColor: hoveredButton === 'add-header' ? COLORS.accent.hover : COLORS.accent.default,
              cursor: 'pointer'
            }}
          >
            <PlusIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8] }} />
            Add Item
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: SPACING[16]
        }}>
          <div style={{
            backgroundColor: COLORS.bg.panel,
            overflow: 'hidden',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`
          }}>
            <div style={{ padding: SPACING[16] }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexShrink: 0 }}>
                  <TagIcon style={{ width: '24px', height: '24px', color: COLORS.text.tertiary }} />
                </div>
                <div style={{ marginLeft: SPACING[16], flex: 1, minWidth: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <dt style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      Total Items
                    </dt>
                    <dd style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      margin: 0
                    }}>
                      {stats.total_items}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: COLORS.bg.panel,
            overflow: 'hidden',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`
          }}>
            <div style={{ padding: SPACING[16] }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexShrink: 0 }}>
                  <CheckIcon style={{ width: '24px', height: '24px', color: COLORS.status.success }} />
                </div>
                <div style={{ marginLeft: SPACING[16], flex: 1, minWidth: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <dt style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      Purchased
                    </dt>
                    <dd style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      margin: 0
                    }}>
                      {stats.purchased_items}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: COLORS.bg.panel,
            overflow: 'hidden',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`
          }}>
            <div style={{ padding: SPACING[16] }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexShrink: 0 }}>
                  <CurrencyDollarIcon style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />
                </div>
                <div style={{ marginLeft: SPACING[16], flex: 1, minWidth: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <dt style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      Total Value
                    </dt>
                    <dd style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      margin: 0
                    }}>
                      {formatCurrency(stats.total_cost)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: COLORS.bg.panel,
            overflow: 'hidden',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`
          }}>
            <div style={{ padding: SPACING[16] }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexShrink: 0 }}>
                  <CurrencyDollarIcon style={{ width: '24px', height: '24px', color: COLORS.status.warning }} />
                </div>
                <div style={{ marginLeft: SPACING[16], flex: 1, minWidth: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <dt style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      Pending Value
                    </dt>
                    <dd style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      margin: 0
                    }}>
                      {formatCurrency(stats.pending_cost)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        padding: SPACING[24],
        border: `1px solid ${COLORS.border.default}`
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: SPACING[16]
        }}>
          <div>
            <label
              htmlFor="search"
              style={{
                display: 'block',
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: COLORS.text.primary
              }}
            >
              Search
            </label>
            <div style={{ marginTop: SPACING[4], position: 'relative' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                paddingLeft: SPACING[12],
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none'
              }}>
                <MagnifyingGlassIcon style={{ width: '20px', height: '20px', color: COLORS.text.tertiary }} />
              </div>
              <input
                type="text"
                id="search"
                style={{
                  display: 'block',
                  width: '100%',
                  paddingLeft: SPACING[40],
                  paddingRight: SPACING[12],
                  paddingTop: SPACING[8],
                  paddingBottom: SPACING[8],
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  boxSizing: 'border-box'
                }}
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="priority"
              style={{
                display: 'block',
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: COLORS.text.primary
              }}
            >
              Priority
            </label>
            <select
              id="priority"
              style={{
                marginTop: SPACING[4],
                display: 'block',
                width: '100%',
                padding: `${SPACING[8]} ${SPACING[12]}`,
                border: `1px solid ${COLORS.border.default}`,
                backgroundColor: COLORS.bg.panel,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.small,
                boxSizing: 'border-box'
              }}
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="status"
              style={{
                display: 'block',
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: COLORS.text.primary
              }}
            >
              Status
            </label>
            <select
              id="status"
              style={{
                marginTop: SPACING[4],
                display: 'block',
                width: '100%',
                padding: `${SPACING[8]} ${SPACING[12]}`,
                border: `1px solid ${COLORS.border.default}`,
                backgroundColor: COLORS.bg.panel,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.small,
                boxSizing: 'border-box'
              }}
              value={filterPurchased}
              onChange={(e) => setFilterPurchased(e.target.value)}
            >
              <option value="">All Items</option>
              <option value="false">Not Purchased</option>
              <option value="true">Purchased</option>
            </select>
          </div>
        </div>
      </div>

      {/* Items List */}
      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: RADIUS,
          padding: SPACING[16]
        }}>
          <p style={{ color: COLORS.status.error, margin: 0 }}>{error}</p>
        </div>
      )}

      <div style={{
        backgroundColor: COLORS.bg.panel,
        overflow: 'hidden',
        borderRadius: RADIUS,
        border: `1px solid ${COLORS.border.default}`
      }}>
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: `${SPACING[48]} 0` }}>
            <TagIcon style={{ margin: '0 auto', width: '48px', height: '48px', color: COLORS.text.tertiary }} />
            <h3 style={{
              marginTop: SPACING[8],
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: COLORS.text.primary
            }}>
              No wish list items
            </h3>
            <p style={{
              marginTop: SPACING[4],
              fontSize: TYPOGRAPHY.body.sizes.small,
              color: COLORS.text.secondary
            }}>
              Get started by adding your first item.
            </p>
            <div style={{ marginTop: SPACING[24] }}>
              <button
                onClick={() => setIsFormOpen(true)}
                onMouseEnter={() => setHoveredButton('add-empty')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  border: 'none',
                  borderRadius: RADIUS,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: '#FFFFFF',
                  backgroundColor: hoveredButton === 'add-empty' ? COLORS.accent.hover : COLORS.accent.default,
                  cursor: 'pointer'
                }}
              >
                <PlusIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8] }} />
                Add Item
              </button>
            </div>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((item, index) => (
              <li
                key={item.id}
                style={{
                  padding: SPACING[24],
                  backgroundColor: item.is_purchased ? COLORS.bg.base : 'transparent',
                  borderTop: index > 0 ? `1px solid ${COLORS.border.default}` : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                      <h3 style={{
                        fontSize: TYPOGRAPHY.size.lg,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: item.is_purchased ? COLORS.text.secondary : COLORS.text.primary,
                        textDecoration: item.is_purchased ? 'line-through' : 'none',
                        margin: 0
                      }}>
                        {item.title}
                      </h3>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `${SPACING[4]} ${SPACING[8]}`,
                        borderRadius: '9999px',
                        fontSize: TYPOGRAPHY.body.sizes.tiny,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        ...getPriorityStyles(item.priority)
                      }}>
                        {item.priority}
                      </span>
                      {item.is_purchased && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `${SPACING[4]} ${SPACING[8]}`,
                          borderRadius: '9999px',
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: '#166534',
                          backgroundColor: '#DCFCE7'
                        }}>
                          Purchased
                        </span>
                      )}
                    </div>

                    <div style={{
                      marginTop: SPACING[8],
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[16],
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary
                    }}>
                      <span style={{ fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                        {formatCurrency(item.cost)}
                      </span>
                      {item.category && (
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <TagIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4] }} />
                          {item.category.name}
                        </span>
                      )}
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4] }} />
                        {formatDate(item.created_at)}
                      </span>
                    </div>

                    {item.comment && (
                      <p style={{
                        marginTop: SPACING[8],
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.secondary
                      }}>
                        {item.comment}
                      </p>
                    )}

                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseEnter={() => setHoveredButton(`link-${item.id}`)}
                        onMouseLeave={() => setHoveredButton(null)}
                        style={{
                          marginTop: SPACING[8],
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: hoveredButton === `link-${item.id}` ? COLORS.accent.hover : COLORS.accent.default,
                          textDecoration: 'none'
                        }}
                      >
                        <ArrowTopRightOnSquareIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4] }} />
                        View Product
                      </a>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                    <button
                      onClick={() => handleMarkPurchased(item)}
                      onMouseEnter={() => setHoveredButton(`purchase-${item.id}`)}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        padding: SPACING[8],
                        borderRadius: '9999px',
                        border: 'none',
                        backgroundColor: hoveredButton === `purchase-${item.id}`
                          ? (item.is_purchased ? '#FEF9C3' : '#DCFCE7')
                          : 'transparent',
                        color: item.is_purchased ? '#CA8A04' : '#16A34A',
                        cursor: 'pointer'
                      }}
                      title={item.is_purchased ? 'Mark as not purchased' : 'Mark as purchased'}
                    >
                      {item.is_purchased
                        ? <XMarkIcon style={{ width: '20px', height: '20px' }} />
                        : <CheckIcon style={{ width: '20px', height: '20px' }} />
                      }
                    </button>

                    <button
                      onClick={() => handleEditItem(item)}
                      onMouseEnter={() => setHoveredButton(`edit-${item.id}`)}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        padding: SPACING[8],
                        borderRadius: '9999px',
                        border: 'none',
                        backgroundColor: hoveredButton === `edit-${item.id}` ? COLORS.bg.hover : 'transparent',
                        color: hoveredButton === `edit-${item.id}` ? COLORS.text.secondary : COLORS.text.tertiary,
                        cursor: 'pointer'
                      }}
                      title="Edit item"
                    >
                      <PencilIcon style={{ width: '20px', height: '20px' }} />
                    </button>

                    <button
                      onClick={() => handleDeleteItem(item)}
                      onMouseEnter={() => setHoveredButton(`delete-${item.id}`)}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        padding: SPACING[8],
                        borderRadius: '9999px',
                        border: 'none',
                        backgroundColor: hoveredButton === `delete-${item.id}` ? '#FEF2F2' : 'transparent',
                        color: hoveredButton === `delete-${item.id}` ? COLORS.status.error : '#F87171',
                        cursor: 'pointer'
                      }}
                      title="Delete item"
                    >
                      <XMarkIcon style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Form Modal */}
      <WishListForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onItemCreated={handleItemCreated}
        initialItem={editingItem}
        isEditMode={!!editingItem}
      />
    </div>
  );
};

export default WishList;
